'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { loadHubSpotForms } from '@/lib/hubspot-forms';
import { runOnFirstInteractionOrTimeout } from '@/lib/deferred-script';

// NOTE: the Magento legacy CSS bundle is NOT imported here even though
// consumers of this component may need both this component AND the bundle.
// Reason: any module that statically imports MagentoRawContentInteractions
// would inherit the CSS as a side effect. Instead, the CSS import lives in
// the route-local files that intentionally consume the bundle (design,
// sensor-enroll, quarterly-update). See src/styles/magento-legacy.css for
// the full consumer list.

const TAB_TRIGGER_SELECTOR = '[data-toggle="tab"], [data-bs-toggle="tab"]';
const RAW_MAGENTO_SCRIPT_TYPE = 'text/x-magento-raw-script';
const MAGENTO_JS_ASSETS = ['/magento-assets/theme-security/js/jquery.min.js', '/magento-assets/theme-security/js/popper.min.js', '/magento-assets/theme-security/js/bootstrap.min.js', '/magento-assets/theme-security/js/slick.min.js', '/magento-assets/theme-security/js/jquery.fancybox.min.js'];
// Markup that only works once the legacy jQuery stack (jQuery, Popper,
// Bootstrap, Slick, Fancybox: ~274 KB) is loaded. Tabs are not here: the native
// click handler below drives them. Executable raw scripts also count
// (see needsLegacyMagentoScripts).
const LEGACY_JS_DEPENDENT_SELECTOR = ['.gallery_active', '#dealsSlider', '[data-fancybox]', '[data-toggle]:not([data-toggle="tab"])', '[data-bs-toggle]:not([data-bs-toggle="tab"])'].join(', ');
const GALLERY_SLICK_OPTIONS = {
  centerMode: true,
  dots: false,
  arrows: false,
  infinite: true,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  responsive: [{
    breakpoint: 1400,
    settings: {
      slidesToShow: 4
    }
  }, {
    breakpoint: 1200,
    settings: {
      slidesToShow: 3
    }
  }, {
    breakpoint: 992,
    settings: {
      slidesToShow: 2,
      centerMode: false
    }
  }, {
    breakpoint: 768,
    settings: {
      slidesToShow: 2,
      centerMode: false
    }
  }, {
    breakpoint: 576,
    settings: {
      slidesToShow: 2
    }
  }]
};
const DEALS_SLICK_OPTIONS = {
  infinite: true,
  speed: 300,
  slidesToShow: 5,
  dots: true,
  centerMode: true,
  autoplay: true,
  arrows: true,
  prevArrow: '<button type="button" class="slick-prev"><img src="https://scw-commerce-media.s3.amazonaws.com/media/wysiwyg/black-friday/arrow-white.png" alt="Previous" /></button>',
  nextArrow: '<button type="button" class="slick-next"><img src="https://scw-commerce-media.s3.amazonaws.com/media/wysiwyg/black-friday/arrow-white.png" alt="Next" /></button>',
  responsive: [{
    breakpoint: 767,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1
    }
  }, {
    breakpoint: 480,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1
    }
  }]
};
type MagentoRequire = ((deps: string | string[], callback?: (...modules: unknown[]) => void) => unknown) & {
  config?: () => MagentoRequire;
};
type MagentoWindow = Window & {
  $?: unknown;
  jQuery?: unknown;
  hbspt?: unknown;
  Popper?: unknown;
  require?: MagentoRequire;
  requirejs?: MagentoRequire;
};
type MagentoJQueryCollection = {
  fancybox?: (options?: unknown) => unknown;
  slick?: (options?: unknown) => unknown;
};
type MagentoJQuery = ((target: Element | Element[] | string) => MagentoJQueryCollection) & {
  fancybox?: unknown;
  fn?: {
    fancybox?: unknown;
    slick?: unknown;
    tab?: unknown;
    tabs?: (...args: unknown[]) => unknown;
  };
};
let magentoAssetsPromise: Promise<void> | null = null;
export function MagentoRawContentInteractions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp6, t0);
  return null;
}

/** True when some .magento-raw-content root has markup or scripts that need the legacy jQuery stack. */
function _temp6() {
  const handleClick = _temp;
  const activateInitialHash = _temp2;
  document.addEventListener("click", handleClick);
  window.addEventListener("hashchange", activateInitialHash);
  activateInitialHash();
  for (const root_0 of document.querySelectorAll(".magento-raw-content")) {
    initializeHubSpotEmbeds(root_0);
  }
  let cancelDeferredLoad = _temp3;
  if (needsLegacyMagentoScripts(document)) {
    cancelDeferredLoad = runOnFirstInteractionOrTimeout(_temp5);
  }
  return () => {
    document.removeEventListener("click", handleClick);
    window.removeEventListener("hashchange", activateInitialHash);
    cancelDeferredLoad();
  };
}
function _temp5() {
  enhanceMagentoContent().catch(_temp4);
}
function _temp4(error) {
  console.warn("Unable to initialize Magento raw content scripts", error);
}
function _temp3() {}
function _temp2() {
  const hash = window.location.hash;
  if (!hash) {
    return;
  }
  const rawContentRoots = document.querySelectorAll(".magento-raw-content");
  for (const root of rawContentRoots) {
    const trigger_0 = findTabTriggerForHash(root, hash);
    if (trigger_0) {
      activateTab(trigger_0);
      return;
    }
  }
}
function _temp(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const trigger = target.closest(TAB_TRIGGER_SELECTOR);
  if (!trigger || !trigger.closest(".magento-raw-content")) {
    return;
  }
  const activated = activateTab(trigger);
  if (activated) {
    event.preventDefault();
  }
}
export function needsLegacyMagentoScripts(scope: ParentNode): boolean {
  for (const root of scope.querySelectorAll('.magento-raw-content')) {
    if (root.querySelector(LEGACY_JS_DEPENDENT_SELECTOR)) {
      return true;
    }
    for (const script of root.querySelectorAll<HTMLScriptElement>('script')) {
      if (isExecutableMagentoScript(script)) {
        return true;
      }
    }
  }
  return false;
}
async function enhanceMagentoContent() {
  if (isScriptLoadingUnavailable()) {
    installRequireShim();
    initializeKnownMagentoPluginsForAllRoots();
    return;
  }
  await loadMagentoAssets();
  installRequireShim();
  await executeMagentoScripts();
}
function isScriptLoadingUnavailable(): boolean {
  return process.env.NODE_ENV === 'test' || navigator.userAgent.toLowerCase().includes('happy-dom');
}
async function loadMagentoAssets() {
  if (!magentoAssetsPromise) {
    magentoAssetsPromise = MAGENTO_JS_ASSETS.reduce((promise, src) => promise.then(() => loadScript(src)), Promise.resolve()).then(() => {
      installRequireShim();
      installJQueryUIShims();
    });
  }
  return magentoAssetsPromise;
}
function installJQueryUIShims() {
  const jQuery = getJQuery();
  if (!jQuery?.fn) {
    return;
  }

  // Magento pages may call jQuery UI's $.fn.tabs() via require(['jquery/ui']).
  // jQuery UI is not bundled — the native [data-toggle="tab"] click handler below
  // drives tab switching, so register a chainable no-op to swallow legacy calls.
  if (typeof jQuery.fn.tabs !== 'function') {
    jQuery.fn.tabs = function () {
      return this;
    };
  }
}
function loadScript(src: string): Promise<void> {
  const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve();
  }
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), {
        once: true
      });
      existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true
      });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.magentoAsset = 'true';
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, {
      once: true
    });
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
      once: true
    });
    document.head.appendChild(script);
  });
}
function installRequireShim() {
  const requireShim: MagentoRequire = ((deps, callback) => {
    const dependencyList = typeof deps === 'string' ? [deps] : deps;
    if (!Array.isArray(dependencyList) || typeof callback !== 'function') {
      return null;
    }
    const runCallback = () => {
      callback(...dependencyList.map(resolveMagentoModule));
    };
    if (dependencyList.some(dependency => dependency === 'domReady' || dependency === 'domReady!')) {
      onDomReady(runCallback);
      return null;
    }
    runCallback();
    return null;
  }) as MagentoRequire;
  requireShim.config = () => requireShim;
  const magentoWindow = getMagentoWindow();
  magentoWindow.require = requireShim;
  magentoWindow.requirejs = requireShim;
}
function resolveMagentoModule(dependency: string): unknown {
  if (dependency === 'domReady' || dependency === 'domReady!') {
    return onDomReady;
  }
  if (dependency === 'jquery' || dependency === 'jquery/ui' || dependency === 'jquery.bootstrap' || dependency === 'slick' || dependency === 'fancybox' || dependency.startsWith('jquery-ui-modules/')) {
    const magentoWindow = getMagentoWindow();
    return magentoWindow.jQuery ?? magentoWindow.$;
  }
  const magentoWindow = getMagentoWindow();
  return magentoWindow.jQuery ?? magentoWindow.$ ?? {};
}
function onDomReady(callback: () => void) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, {
      once: true
    });
    return;
  }
  callback();
}
async function executeMagentoScripts() {
  const roots = document.querySelectorAll('.magento-raw-content');
  for (const root of roots) {
    const scripts = root.querySelectorAll<HTMLScriptElement>('script');
    for (const script of scripts) {
      if (!isExecutableMagentoScript(script) || script.dataset.magentoExecuted === 'true') {
        continue;
      }
      script.dataset.magentoExecuted = 'true';
      try {
        await executeMagentoScript(script);
      } catch (error) {
        console.warn('Unable to execute Magento raw content script', error);
      }
    }
    initializeKnownMagentoPlugins(root);
  }
}
function isExecutableMagentoScript(script: HTMLScriptElement): boolean {
  const type = script.type.trim().toLowerCase();
  return !type || type === 'text/javascript' || type === 'application/javascript' || type === 'application/ecmascript' || type === 'module' || type === RAW_MAGENTO_SCRIPT_TYPE;
}
function executeMagentoScript(sourceScript: HTMLScriptElement): Promise<void> {
  if (sourceScript.src) {
    return loadExternalMagentoScript(sourceScript);
  }
  if (sourceScript.textContent?.includes('hbspt.forms.create') && !getMagentoWindow().hbspt) {
    return loadHubSpotForms().then(() => executeInlineMagentoScript(sourceScript));
  }
  return executeInlineMagentoScript(sourceScript);
}
function executeInlineMagentoScript(sourceScript: HTMLScriptElement): Promise<void> {
  const executableScript = document.createElement('script');
  const originalType = sourceScript.dataset.magentoOriginalType;
  if (originalType && originalType !== 'text/javascript') {
    executableScript.type = originalType;
  }
  executableScript.text = sourceScript.textContent ?? '';
  executableScript.dataset.magentoExecutedInline = 'true';
  const executionParent = sourceScript.parentNode ?? document.body;
  executionParent.insertBefore(executableScript, sourceScript.nextSibling);
  executableScript.remove();
  return Promise.resolve();
}
function loadExternalMagentoScript(sourceScript: HTMLScriptElement): Promise<void> {
  const src = normalizeScriptSrc(sourceScript);
  const existingScript = document.querySelector<HTMLScriptElement>(`script[data-magento-executed-src="${src}"]`);
  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve();
  }
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), {
        once: true
      });
      existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true
      });
    });
  }
  return new Promise((resolve, reject) => {
    const executableScript = document.createElement('script');
    for (const attribute of Array.from(sourceScript.attributes)) {
      if (attribute.name === 'type' || attribute.name === 'data-magento-raw-script') {
        continue;
      }
      if (attribute.name === 'data-magento-original-type') {
        if (attribute.value && attribute.value !== 'text/javascript') {
          executableScript.type = attribute.value;
        }
        continue;
      }
      executableScript.setAttribute(attribute.name, attribute.name === 'src' ? src : attribute.value);
    }
    executableScript.async = false;
    executableScript.dataset.magentoExecutedSrc = src;
    executableScript.addEventListener('load', () => {
      executableScript.dataset.loaded = 'true';
      resolve();
    }, {
      once: true
    });
    executableScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
      once: true
    });
    document.body.appendChild(executableScript);
  });
}
function normalizeScriptSrc(sourceScript: HTMLScriptElement): string {
  const sourceAttribute = sourceScript.getAttribute('src');
  if (sourceAttribute?.startsWith('//')) {
    return `https:${sourceAttribute}`;
  }
  return sourceScript.src;
}
function initializeKnownMagentoPlugins(root: Element) {
  initializeHubSpotEmbeds(root);
  const jQuery = getJQuery();
  if (!jQuery) {
    return;
  }
  if (typeof jQuery.fn?.slick === 'function') {
    for (const slider of root.querySelectorAll<HTMLElement>('.gallery_active:not(.slick-initialized)')) {
      jQuery(slider).slick?.(GALLERY_SLICK_OPTIONS);
    }
    for (const slider of root.querySelectorAll<HTMLElement>('#dealsSlider:not(.slick-initialized)')) {
      jQuery(slider).slick?.(DEALS_SLICK_OPTIONS);
    }
  }
  if (typeof jQuery.fn?.fancybox === 'function') {
    const fancyboxLinks = Array.from(root.querySelectorAll<HTMLElement>('[data-fancybox]'));
    if (fancyboxLinks.length > 0) {
      jQuery(fancyboxLinks).fancybox?.();
    }
  }
}
function initializeKnownMagentoPluginsForAllRoots() {
  for (const root of document.querySelectorAll('.magento-raw-content')) {
    initializeKnownMagentoPlugins(root);
  }
}
function initializeHubSpotEmbeds(root: Element) {
  replaceLegacyHubSpotObjects(root);
  for (const iframe of root.querySelectorAll<HTMLIFrameElement>('iframe[src*="thankyou.getscw.com"]')) {
    iframe.dataset.hubspotEmbed = 'iframe';
    if (!iframe.title) {
      iframe.title = 'SCW HubSpot embedded page';
    }
    if (!iframe.getAttribute('loading')) {
      iframe.setAttribute('loading', 'lazy');
    }
    if (!iframe.getAttribute('referrerpolicy')) {
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    }
    if (!iframe.style.width && !iframe.getAttribute('width')) {
      iframe.style.width = '100%';
    }
    if (!iframe.style.minHeight && !iframe.style.height && !iframe.getAttribute('height')) {
      iframe.style.minHeight = '600px';
    }
    if (!iframe.style.border) {
      iframe.style.border = '0px';
    }
  }
}
function replaceLegacyHubSpotObjects(root: Element) {
  const objects = root.querySelectorAll<HTMLObjectElement>('object[data*="thankyou.getscw.com"]');
  for (const object of objects) {
    const url = object.getAttribute('data');
    if (!url) {
      continue;
    }
    const iframe = document.createElement('iframe');
    iframe.src = url.startsWith('//') ? `https:${url}` : url;
    iframe.title = object.getAttribute('title') || 'SCW HubSpot embedded page';
    iframe.dataset.hubspotEmbed = 'iframe';
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute('frameborder', '0');
    copyAttribute(object, iframe, 'class');
    copyAttribute(object, iframe, 'width');
    copyAttribute(object, iframe, 'height');
    const style = object.getAttribute('style');
    if (style) {
      iframe.setAttribute('style', style);
    }
    if (!iframe.style.width && !iframe.getAttribute('width')) {
      iframe.style.width = '100%';
    }
    if (!iframe.style.minHeight && !iframe.style.height && !iframe.getAttribute('height')) {
      iframe.style.minHeight = '600px';
    }
    if (!iframe.style.border) {
      iframe.style.border = '0px';
    }
    object.replaceWith(iframe);
  }
}
function copyAttribute(source: Element, target: Element, name: string) {
  const value = source.getAttribute(name);
  if (value) {
    target.setAttribute(name, value);
  }
}
function getJQuery(): MagentoJQuery | null {
  const magentoWindow = getMagentoWindow();
  return typeof magentoWindow.jQuery === 'function' ? magentoWindow.jQuery as MagentoJQuery : null;
}
function getMagentoWindow(): MagentoWindow {
  return window as MagentoWindow;
}
function activateTab(trigger: HTMLElement): boolean {
  const rawContentRoot = trigger.closest('.magento-raw-content');
  const tabList = trigger.closest('[role="tablist"], .nav-tabs, .nav-pills');
  const pane = findTargetPane(trigger, rawContentRoot);
  const tabContent = pane?.closest('.tab-content');
  if (!rawContentRoot || !tabList || !pane || !tabContent) {
    return false;
  }
  deactivateSiblingTabs(tabList);
  deactivateSiblingPanes(tabContent);
  trigger.classList.add('active');
  trigger.setAttribute('aria-selected', 'true');
  trigger.closest('.nav-item')?.classList.add('active');
  pane.classList.add('active', 'show');
  pane.removeAttribute('hidden');
  trigger.dispatchEvent(new CustomEvent('shown.bs.tab', {
    bubbles: true
  }));
  return true;
}
function deactivateSiblingTabs(tabList: Element) {
  const triggers = tabList.querySelectorAll<HTMLElement>(TAB_TRIGGER_SELECTOR);
  for (const trigger of triggers) {
    trigger.classList.remove('active');
    trigger.setAttribute('aria-selected', 'false');
    trigger.closest('.nav-item')?.classList.remove('active', 'show');
  }
}
function deactivateSiblingPanes(tabContent: Element) {
  for (const child of Array.from(tabContent.children)) {
    if (!(child instanceof HTMLElement) || !child.classList.contains('tab-pane')) {
      continue;
    }
    child.classList.remove('active', 'show');
    child.setAttribute('hidden', '');
  }
}
function findTargetPane(trigger: HTMLElement, rawContentRoot: Element | null): HTMLElement | null {
  const targetId = getTargetId(trigger);
  if (!targetId || !rawContentRoot) {
    return null;
  }
  const panes = rawContentRoot.querySelectorAll<HTMLElement>('.tab-pane[id]');
  for (const pane of panes) {
    if (pane.id === targetId) {
      return pane;
    }
  }
  return null;
}
function findTabTriggerForHash(root: Element, hash: string): HTMLElement | null {
  const targetId = hash.replace(/^#/, '');
  if (!targetId) {
    return null;
  }
  const triggers = root.querySelectorAll<HTMLElement>(TAB_TRIGGER_SELECTOR);
  for (const trigger of triggers) {
    if (getTargetId(trigger) === targetId) {
      return trigger;
    }
  }
  return null;
}
function getTargetId(trigger: HTMLElement): string | null {
  const target = trigger.getAttribute('data-target') ?? trigger.getAttribute('data-bs-target') ?? trigger.getAttribute('href') ?? trigger.getAttribute('aria-controls');
  if (!target) {
    return null;
  }
  return target.replace(/^#/, '');
}